import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import './ProjectPage.css';
import ProjectHeader from '../../components/ProjectHeader/ProjectHeader';
import SkeletonCard from '../../components/SkeletonLoaders/SkeletonCard/SkeletonCard';
import ProjectPageSkeleton from '../../components/SkeletonLoaders/ProjectPageSkeleton/ProjectPageSkeleton';
import ProjectCompletionOverlay from '../../components/ProjectCompletionOverlay';
import CompanyList from '../../components/CompanyList/CompanyList';

// Debounce utility
function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef();
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; }, [callback]);
  return useCallback((...args) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);
}

const ProjectPage = ({ config, projectId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = window.history.pushState ? null : undefined; // fallback if needed

  // Parse query params from URL
  function getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      page: parseInt(params.get('page') || '1', 10) - 1,
      q: params.get('q') || '',
      category: params.get('category') || '',
    };
  }

  const initialParams = getQueryParams();
  // Ensure page is never negative
  const safeInitialPage = Math.max(0, initialParams.page);
  const [searchTerm, setSearchTerm] = useState(initialParams.q);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(safeInitialPage);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(initialParams.category);
  const [allCompanies, setAllCompanies] = useState([]); // all results for summary
  const [allLocalCount, setAllLocalCount] = useState(0);

  // Set PAGE_SIZE to a number divisible by 3
  const PAGE_SIZE = 9;

  // Category filter constants
  const CATEGORY_DISPLAY_NAMES = {
    'bygg_och_konstruktion': 'Bygg & Konstruktion',
    'teknik_och_underhall': 'Teknik & Underhåll',
    'transport_och_fordon': 'Transport & Fordon',
    'affarstjanster_och_support': 'Affärstjänster & Support',
    'restaurang_och_catering': 'Restaurang & Catering',
    'boende': 'Boende'
  };
  const CATEGORY_KEYS = Object.keys(CATEGORY_DISPLAY_NAMES);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Only update URL when search is explicitly performed
  const updateUrl = (term, cat, pg) => {
    const params = new URLSearchParams();
    if (term) params.set('q', term);
    if (cat) params.set('category', cat);
    if (pg > 0) params.set('page', (pg + 1).toString());
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (window.location.search !== `?${qs}`) {
      window.history.replaceState({}, '', newUrl);
    }
  };

  // On mount, restore state from URL (already handled by initial useState)

  useEffect(() => {
    if (config && config.coordinates) {
      fetchCompanies(searchTerm, page, selectedCategory);
    }
    // eslint-disable-next-line
  }, [config]);

  // Haversine formula to calculate distance between two lat/lng points in km
  function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    if (
      typeof lat1 !== 'number' || typeof lon1 !== 'number' ||
      typeof lat2 !== 'number' || typeof lon2 !== 'number'
    ) {
      return null;
    }
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d;
  }

  const LOCAL_RADIUS_KM = 50; // threshold for 'local' in km

  const fetchCompanies = async (term, pageNum, category) => {
    if (!config || !config.coordinates) return;
    setLoading(true);
    // Sync state (for category/search/page changes)
    setSearchTerm(term);
    setSelectedCategory(category);
    setPage(pageNum);
    try {
      // Fetch all results for summary (up to cap)
      const cap = 1000;
      const paramsAll = new URLSearchParams({
        q: term,
        lat: config.coordinates.lat,
        lon: config.coordinates.lng,
        from: 0,
        size: cap,
      });
      if (category) paramsAll.append('category', category);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const resAll = await fetch(`${backendUrl}/api/search-users?${paramsAll.toString()}`);
      const dataAll = await resAll.json();
      const projectLat = Number(config.coordinates.lat);
      const projectLng = Number(config.coordinates.lng);
      const allCompanyList = dataAll.profiles || dataAll.items || dataAll.docs || [];
      const allCompaniesWithDistance = allCompanyList.map(item => {
        const loc = item.PublicData_y?.location || item.data?.PublicData_y?.location || {};
        const companyLat = Number(loc.latitude);
        const companyLng = Number(loc.longitude);
        const distance = getDistanceFromLatLonInKm(projectLat, projectLng, companyLat, companyLng);
        const isLocal = typeof distance === 'number' && distance <= LOCAL_RADIUS_KM;
        return {
          data: item,
          distanceFromProject: typeof distance === 'number' ? distance : null,
          isLocal,
        };
      });
      setAllCompanies(allCompaniesWithDistance);
      setAllLocalCount(allCompaniesWithDistance.filter(c => c.isLocal).length);
      // Now fetch paginated companies for the current page
      const params = new URLSearchParams({
        q: term,
        lat: config.coordinates.lat,
        lon: config.coordinates.lng,
        from: pageNum * PAGE_SIZE,
        size: PAGE_SIZE,
      });
      if (category) params.append('category', category);
      const res = await fetch(`${backendUrl}/api/search-users?${params.toString()}`);
      const data = await res.json();
      const companyList = data.profiles || data.items || data.docs || [];
      const companiesWithDistance = companyList.map(item => {
        const loc = item.PublicData_y?.location || item.data?.PublicData_y?.location || {};
        const companyLat = Number(loc.latitude);
        const companyLng = Number(loc.longitude);
        const distance = getDistanceFromLatLonInKm(projectLat, projectLng, companyLat, companyLng);
        const isLocal = typeof distance === 'number' && distance <= LOCAL_RADIUS_KM;
        return {
          data: item,
          distanceFromProject: typeof distance === 'number' ? distance : null,
          isLocal,
        };
      });
      companiesWithDistance.sort((a, b) => {
        if (a.distanceFromProject === null) return 1;
        if (b.distanceFromProject === null) return -1;
        return a.distanceFromProject - b.distanceFromProject;
      });
      setCompanies(companiesWithDistance);
      setTotal(data.total || 0);
    } catch (e) {
      setCompanies([]);
      setTotal(0);
      setAllCompanies([]);
      setAllLocalCount(0);
    } finally {
      setLoading(false);
    }
  };


  // Count local and total companies for all pages
  const localCount = allLocalCount;
  const totalCount = allCompanies.length;


  // Category filter UI
  const handleCategorySelect = (cat) => {
    const newCategory = cat === selectedCategory ? '' : cat;
    setSelectedCategory(newCategory);
    setPage(0);
    updateUrl(searchTerm, newCategory, 0);
    fetchCompanies(searchTerm, 0, newCategory);
  };

  // Only trigger search on explicit action
  const handleSearch = (e) => {
    const value = e.target.value !== undefined ? e.target.value : searchTerm;
    setSearchTerm(value);
    setPage(0);
    updateUrl(value, selectedCategory, 0);
    fetchCompanies(value, 0, selectedCategory);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    updateUrl(searchTerm, selectedCategory, newPage);
    fetchCompanies(searchTerm, newPage, selectedCategory);
  };

  if (!config) {
    return <ProjectPageSkeleton />;
  }

  const projectTranslations = {
    title: t(`pages.projects.${projectId}.title`),
    description: t(`pages.projects.${projectId}.description`),
    projectDescription: t(`pages.projects.${projectId}.projectDescription`),
    location: t(`pages.projects.${projectId}.location`),
    status: t(`pages.projects.${projectId}.projectStatus`),
  };

  const getProfileLink = (profileId) => {
    const languagePrefix = location.pathname.startsWith('/en') ? '/en' : '';
    return `${languagePrefix}/profile/${profileId}`;
  };

  return (
    <div className="project-page" data-testid="project-page">
      <ProjectHeader
        config={config}
        title={projectTranslations.title}
        description={projectTranslations.description}
        projectDescription={projectTranslations.projectDescription}
        banner={config.headerImage}
        projectLink={config.projectLink}
        data-testid="project-header"
        getProfileLink={getProfileLink}
      />
      <section className="project-search-section">
        <h2 className="project-search-heading">{t('components.ProjectPage.findLocalSuppliers', 'Hitta lokala leverantörer')}</h2>
        <div className="project-search-filter-bar">
          {/* --- Universal Search Bar Style --- */}
          <div className="universal-search-bar-wrapper">
            <form
              className="universal-search-bar-input-container"
              onSubmit={e => {
                e.preventDefault();
                handleSearch({ target: { value: searchTerm } });
              }}
              role="search"
            >
              <input
                type="text"
                className="universal-search-bar-input"
                placeholder={t('components.ProjectPage.searchPlaceholder', 'Sök företag eller kategori...')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                aria-label="Sök företag eller kategori"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch({ target: { value: searchTerm } });
                  }
                }}
              />
              <button
                type="submit"
                className="universal-search-bar-btn"
                tabIndex={0}
                aria-label="Sök"
                onClick={handleSearch}
              >
                <FaSearch size={28} />
              </button>
            </form>
          </div>
          {/* --- End Universal Search Bar Style --- */}
          <div className="category-filter-row">
            {CATEGORY_KEYS.map(cat => (
              <button
                key={cat}
                className={`category-filter-btn${selectedCategory === cat ? ' selected' : ''}`}
                onClick={() => handleCategorySelect(cat)}
                type="button"
              >
                {CATEGORY_DISPLAY_NAMES[cat]}
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Results summary */}
      <div style={{ textAlign: 'center', margin: '1rem 0 0.5rem 0', fontWeight: 500 }}>
        {loading
          ? t('components.ProjectPage.loadingResults', 'Hämtar resultat...')
          : t('components.ProjectPage.resultsSummary', {
              local: localCount,
              total: totalCount,
              defaultValue: `${localCount} lokala av ${totalCount} resultat`
            })
        }
      </div>
      <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        {/* Loading indication */}
        {loading && (
          <div className="company-list-grid" aria-live="polite" aria-busy="true">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}
        <div className="company-list-grid">
          {(!loading && companies.length === 0) ? (
            <div className="no-results-message">
              {t('components.ProjectPage.noResults', 'Inga företag matchar din sökning. Försök med ett annat sökord eller filtrera på en annan kategori.')}
            </div>
          ) : (
            !loading && <CompanyList companies={companies} />
          )}
        </div>
      </div>
      {/* Pagination controls */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
          <button onClick={() => handlePageChange(page - 1)} disabled={page === 0}>
            {t('components.ProjectPage.prev')}
          </button>
          <span style={{ margin: '0 1rem' }}>{page + 1} / {Math.ceil(total / PAGE_SIZE)}</span>
          <button onClick={() => handlePageChange(page + 1)} disabled={(page + 1) * PAGE_SIZE >= total}>
            {t('components.ProjectPage.next')}
          </button>
        </div>
      )}
      {/* ProjectInfo now shown as modal, not inline */}
      {config.finished && (
        <ProjectCompletionOverlay projectName={projectTranslations.title} />
      )}
    </div>
  );
};

export default ProjectPage;
